import {
    createUrl,
    findMarginSwitchTimestamp,
    withMarginColumns,
} from '@/lib/helpers/hypertrackerHelpers';
import { useQueryFetcher } from '@/lib/query/useQueryFetcher';
import { HT_API_ROUTES } from '@/lib/http/hypertrackerHttpRequest';
import {
    HomeSegmentByAge,
    HomeSegmentData,
    SegmentsBiasMetrics,
    SegmentsData,
    DATA_TYPES,
    RANGE_TYPES,
} from '@/lib/types/hypertrackerTypes';
import { useMemo } from 'react';
import useIsMemberAuthorized from '@/hooks/useIsMemberAuthorized';
import { useSelector } from 'react-redux';
import { RootState } from '@/store/store';
import {
    selectHypertrackerFilters,
    selectPositionRecencyToggle,
} from '@/state/hypertracker/hypertrackerSelector';

type WalletsResponse = {
    items: any[];
    totalCount: number;
};

type SegmentJsonData = {
    largestPositions: any[];
    liquidationRiskByAsset: any[];
    historyData?: any;
    historyPositionsData?: any;
    [key: string]: any;
};

// Wallets are assigned to cohorts by perp margin equity, so cohort equity, bias and exposure
// are shown on perp margin equity too: the margin values go into the fields the UI reads.
const withMarginSegmentValues = <T extends HomeSegmentData | HomeSegmentByAge>(segment: T): T => ({
    ...segment,
    activePerpEquity: segment.activePerpMarginEquity,
    perpBias: segment.marginPerpBias,
    exposureRatio: segment.marginExposureRatio,
});

type OpenPerpsSummaryJson = {
    // null when the cohort has no active margin equity
    marginPerpBias: number | null;
    marginExposureRatio: number;
    totalActivePerpMarginEquity: number;
    totalPerpMarginEquity?: number;
};

const withMarginOpenPerpsSummary = (summary?: OpenPerpsSummaryJson) =>
    summary && {
        ...summary,
        perpBias: summary.marginPerpBias,
        exposureRatio: summary.marginExposureRatio,
        totalActivePerpEquity: summary.totalActivePerpMarginEquity,
        totalPerpEquity: summary.totalPerpMarginEquity,
    };

type BiasChart = { segmentId: number; columns: string[]; data: [string, number, number][] };

const biasToMargin = { bias: 'marginBias' };
const historyPositionsToMargin = {
    bias: 'marginBias',
    exposureRatio: 'marginExposureRatio',
    activePerpEquity: 'activePerpMarginEquity',
};

const restructureBiasMetrics = (charts: BiasChart[]) => {
    const restructured: SegmentsBiasMetrics = {};
    charts.forEach(({ segmentId, columns, data }) => {
        const marginSwitchTimestamp = findMarginSwitchTimestamp(columns, data, biasToMargin);
        restructured[segmentId] = data.map((row) => {
            const [timestamp, bias] = withMarginColumns(columns, row, biasToMargin);
            return [timestamp, bias, timestamp === marginSwitchTimestamp];
        });
    });
    return restructured;
};

const getSegmentsJsonName = (rangeType: RANGE_TYPES) => {
    switch (rangeType) {
        case RANGE_TYPES.DAY:
            return '/segments_bias_charts_data_24h.json';
        case RANGE_TYPES.WEEK:
            return '/segments_bias_charts_data_7d.json';
        case RANGE_TYPES.MONTH:
            return '/segments_bias_charts_data_30d.json';
        default:
            return '/segments_bias_charts_data.json';
    }
};

const useSegmentsData = ({
    segmentId,
    selectedAge,
    positionAgeByPerpEquity,
    positionAgeByAllTimePnl,
}: {
    segmentId?: number;
    selectedAge?: RANGE_TYPES;
    positionAgeByPerpEquity?: RANGE_TYPES;
    positionAgeByAllTimePnl?: RANGE_TYPES;
} = {}) => {
    const isMemberAuthorized = useIsMemberAuthorized();
    const filters = useSelector((state: RootState) => selectHypertrackerFilters(state));
    const positionRecency = useSelector(selectPositionRecencyToggle);

    const segmentsDataQuery = useQueryFetcher<SegmentsData>({
        mode: 'json',
        buildJsonUrl: () => createUrl('/segments.json'),
    });

    const statusDataQuery = useQueryFetcher<SegmentsData>({
        mode: 'json',
        buildJsonUrl: () => createUrl('/status.json'),
    });

    const biasMetricsDataQuery = useQueryFetcher<any>({
        mode: 'json',
        buildJsonUrl: () => createUrl(getSegmentsJsonName(positionRecency)),
        enabled: !!positionRecency,
    });

    const biasMetricsDataByPerpEquityQuery = useQueryFetcher<any>({
        mode: 'json',
        buildJsonUrl: () =>
            createUrl(getSegmentsJsonName(positionAgeByPerpEquity || RANGE_TYPES.DAY)),
        enabled: !!positionAgeByPerpEquity,
    });

    const biasMetricsDataByAllTimePnlQuery = useQueryFetcher<any>({
        mode: 'json',
        buildJsonUrl: () =>
            createUrl(getSegmentsJsonName(positionAgeByAllTimePnl || RANGE_TYPES.DAY)),
        enabled: !!positionAgeByAllTimePnl,
    });

    const segmentsSummaryData = useMemo(() => {
        if (!segmentsDataQuery.data || !statusDataQuery.data) return null;
        if (segmentsDataQuery.error || statusDataQuery.error)
            throw segmentsDataQuery.error || statusDataQuery.error;
        const merged = { ...segmentsDataQuery.data, ...statusDataQuery.data };
        return {
            ...merged,
            segments: merged.segments?.map((segment) => ({
                ...withMarginSegmentValues(segment),
                perpEquity: segment.perpMarginEquity,
            })),
            segments24h: merged.segments24h?.map(withMarginSegmentValues),
            segments7d: merged.segments7d?.map(withMarginSegmentValues),
            segments30d: merged.segments30d?.map(withMarginSegmentValues),
        };
    }, [segmentsDataQuery.data, statusDataQuery.data]);

    const biasMetricsData = useMemo(() => {
        if (!biasMetricsDataQuery.data) return null;
        if (biasMetricsDataQuery.error) throw biasMetricsDataQuery.error;
        return restructureBiasMetrics(biasMetricsDataQuery.data);
    }, [biasMetricsDataQuery.data]);

    const biasMetricsByPerpEquityData = useMemo(() => {
        if (!biasMetricsDataByPerpEquityQuery.data) return null;
        if (biasMetricsDataByPerpEquityQuery.error) throw biasMetricsDataByPerpEquityQuery.error;
        return restructureBiasMetrics(biasMetricsDataByPerpEquityQuery.data);
    }, [biasMetricsDataByPerpEquityQuery.data]);

    const biasMetricsByAllTimePnlData = useMemo(() => {
        if (!biasMetricsDataByAllTimePnlQuery.data) return null;
        if (biasMetricsDataByAllTimePnlQuery.error) throw biasMetricsDataByAllTimePnlQuery.error;
        return restructureBiasMetrics(biasMetricsDataByAllTimePnlQuery.data);
    }, [biasMetricsDataByAllTimePnlQuery.data]);

    const segmentData = useMemo(() => {
        if (!segmentsSummaryData || !segmentId) return null;
        return segmentsSummaryData.segments.find((item) => item.segment.id === segmentId);
    }, [segmentsSummaryData, segmentId]);

    const category = segmentData?.segment?.category as DATA_TYPES | undefined;

    const singleSegmentQuery = useQueryFetcher<SegmentJsonData | null>({
        mode: 'json',
        buildJsonUrl: () => createUrl(`/segment_${segmentId ?? 0}.json`),
        enabled: !!segmentId,
    });

    const singleSegmentWithAgeQuery = useQueryFetcher<SegmentJsonData | null>({
        mode: 'json',
        buildJsonUrl: () =>
            createUrl(
                `/segment_${segmentId ?? 0}${selectedAge === 'all' ? '' : `_${selectedAge ?? ''}`}.json`,
            ),
        enabled: !!segmentId && !!selectedAge,
    });

    const segmentWalletsJsonQuery = useQueryFetcher<any[] | null>({
        mode: 'json',
        buildJsonUrl: () => createUrl(`/segment_${segmentId ?? 0}_wallets.json`),
        enabled: !!segmentId && !isMemberAuthorized,
    });

    const walletsApiQuery = useQueryFetcher<WalletsResponse | null>({
        mode: 'api',
        apiPath: HT_API_ROUTES.SEGMENTS.WALLETS(segmentId ?? 0),
        params:
            segmentId && category
                ? (() => {
                      const { pnlSegmentIds, sizeSegmentIds, ...rest } = filters.wallets;
                      return {
                          segmentId,
                          ...rest,
                          segmentIds: category === DATA_TYPES.SIZE ? pnlSegmentIds : sizeSegmentIds,
                      };
                  })()
                : undefined,
        isAuthorized: isMemberAuthorized,
        enabled: !!segmentId && !!category,
    });

    const singleSegmentData = useMemo((): Omit<
        SegmentJsonData,
        'largestPositions' | 'liquidationRiskByAsset'
    > | null => {
        if (!segmentId) return null;
        if (!singleSegmentQuery.data) return null;
        const { largestPositions, liquidationRiskByAsset, ...rest } = singleSegmentQuery.data;
        const merged = { ...rest, ...(singleSegmentWithAgeQuery.data ?? {}) };
        const history = merged.historyPositionsData;
        return {
            ...merged,
            openPerpsSummary: withMarginOpenPerpsSummary(merged.openPerpsSummary),
            historyPositionsData: history && {
                ...history,
                data: history.data.map((row) =>
                    withMarginColumns(history.columns, row, historyPositionsToMargin),
                ),
                marginSwitchTimestamp: findMarginSwitchTimestamp(
                    history.columns,
                    history.data,
                    historyPositionsToMargin,
                ),
            },
        };
    }, [singleSegmentQuery.data, segmentId, selectedAge, singleSegmentWithAgeQuery.data]);

    const largestPositionsData = useMemo(() => {
        if (!segmentId) return null;

        if (!singleSegmentWithAgeQuery.data?.largestPositions) return null;
        return {
            data: singleSegmentWithAgeQuery.data.largestPositions,
            totalCount: 50,
        };
    }, [selectedAge, singleSegmentWithAgeQuery.data, segmentId]);

    const liquidationRiskData = useMemo(() => {
        if (!segmentId) return null;

        if (!singleSegmentWithAgeQuery.data?.liquidationRiskByAsset) return null;
        return {
            data: singleSegmentWithAgeQuery.data.liquidationRiskByAsset,
            totalCount: 50,
        };
    }, [singleSegmentWithAgeQuery.data, segmentId, selectedAge]);

    const walletsData = useMemo(() => {
        if (!segmentId) return null;

        if (isMemberAuthorized) {
            if (!walletsApiQuery.data) return null;
            return {
                data: walletsApiQuery.data.items,
                totalCount: walletsApiQuery.data.totalCount,
            };
        } else {
            if (!segmentWalletsJsonQuery.data) return null;
            return {
                data: segmentWalletsJsonQuery.data,
                totalCount: 50,
            };
        }
    }, [isMemberAuthorized, walletsApiQuery.data, segmentWalletsJsonQuery.data, segmentId]);

    return {
        segmentsSummaryData,
        biasMetricsData,
        biasMetricsByPerpEquityData,
        biasMetricsByAllTimePnlData,
        singleSegmentData,
        largestPositionsData,
        liquidationRiskData,
        walletsData,
        walletsLoading: walletsApiQuery.isPlaceholderData,
    };
};

export default useSegmentsData;
